import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Play,
  Award,
  FileCheck,
  Users,
  CreditCard,
  Library,
  Video,
  LayoutDashboard,
  Zap,
} from 'lucide-react';

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347" />
  </svg>
);

type Feature = {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
};

const PAUSE_AFTER_INTERACTION_MS = 6000;

const CourseFeatures = () => {
  const features: Feature[] = [
    {
      icon: Play,
      title: 'HD-Aufzeichnungen',
      description: 'Alle Unterrichtseinheiten werden aufgezeichnet. Inhalte jederzeit wiederholen oder verpasste Sessions nachholen.',
    },
    {
      icon: WhatsAppIcon,
      title: 'WhatsApp-Gruppen',
      description: 'Direkter Austausch mit Lehrern und Mitschülern. Stelle Fragen und bleib konstant dran.',
    },
    {
      icon: Award,
      title: 'Zertifikat',
      description: 'Nach bestandener Prüfung erhältst du ein offizielles Arabicroots-Zertifikat.',
    },
    {
      icon: FileCheck,
      title: 'Prüfungen & Tests',
      description: 'Durch Zwischen- und Abschlussprüfungen kannst du dein Wissen festigen und sichtbar machen.',
    },
    {
      icon: Users,
      title: 'Kleine Gruppen',
      description: '4–8 Teilnehmer pro Gruppe — persönliche Betreuung und gezieltes Feedback.',
    },
    {
      icon: CreditCard,
      title: 'Flexible Zahlung',
      description: 'Monatlich oder einmalig über verschiedene Zahlungswege.',
    },
    {
      icon: Video,
      title: 'Live-Interaktion',
      description: 'Echte Live-Sessions mit direktem Austausch — stelle Fragen und entwickle Sicherheit.',
    },
    {
      icon: Library,
      title: 'Bewährte Lehrwerke',
      description: 'Madina-Bücher und Arabiyyah Bayna Yadayk — strukturiert und erprobt.',
    },
    {
      icon: LayoutDashboard,
      title: 'Schülerbereich',
      description: 'Dein persönlicher Bereich mit allen Lektionen, Materialien, Zertifikaten und Live-Terminen — übersichtlich auf jedem Gerät.',
    },
    {
      icon: Zap,
      title: 'Lern-App',
      description: 'Vokabeln und Wörterbuch direkt auf dem Handy — lerne auch unterwegs. Exklusiv für unsere Schüler.',
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const [cardWidth, setCardWidth] = useState(308);
  const pausedUntilRef = useRef<number>(0);
  const directionRef = useRef<1 | -1>(1);

  const gap = 16;
  const step = cardWidth + gap;

  useEffect(() => {
    const update = () => {
      if (window.innerWidth >= 1280) { setVisibleCount(3); setCardWidth(308); }
      else if (window.innerWidth >= 768) { setVisibleCount(2); setCardWidth(308); }
      else { setVisibleCount(1); setCardWidth(Math.min(window.innerWidth - 32, 340)); }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const maxIndex = Math.max(features.length - visibleCount, 0);

  useEffect(() => {
    if (currentIndex > maxIndex) setCurrentIndex(0);
  }, [currentIndex, maxIndex]);

  // Manual navigation — pauses auto-slide for PAUSE_AFTER_INTERACTION_MS
  const goTo = useCallback((i: number) => {
    pausedUntilRef.current = Date.now() + PAUSE_AFTER_INTERACTION_MS;
    setCurrentIndex(i);
  }, []);

  // Auto-slide — respects the pause window
  useEffect(() => {
    const id = window.setInterval(() => {
      if (Date.now() < pausedUntilRef.current) return;
      setCurrentIndex(prev => {
        const next = prev + directionRef.current;
        if (next >= maxIndex) { directionRef.current = -1; return maxIndex; }
        if (next <= 0) { directionRef.current = 1; return 0; }
        return next;
      });
    }, 4500);
    return () => window.clearInterval(id);
  }, [maxIndex]);

  return (
    <section id="course-features" className="bg-white py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-8 md:mb-12">
          <p className="text-xs font-semibold tracking-[0.12em] uppercase text-[#970000] mb-3">
            Mehr als nur Unterricht
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-[-0.04em] leading-[1.05] text-[#1a0606]">
            Arabisch lernen mit System –{' '}
            <br className="hidden sm:inline" />
            <span className="oblique font-extrabold text-[#970000] tracking-[-0.025em]">was dich wirklich voranbringt</span>.
          </h2>
          <p className="mt-3 md:mt-4 text-[15px] md:text-[17px] leading-relaxed text-gray-500">
            Kein Videokurs, kein Chaos – Arabischunterricht live, strukturiert und auf Deutsch.
          </p>
        </div>

        {/* Slider */}
        <div className="flex flex-col items-center">
          <div
            className="overflow-hidden w-full"
            style={{ maxWidth: `${visibleCount * cardWidth + (visibleCount - 1) * gap}px` }}
          >
            <div
              className="flex transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
              style={{ transform: `translateX(-${currentIndex * step}px)`, gap: `${gap}px` }}
            >
              {features.map((feature, index) => (
                <div key={index} className="shrink-0" style={{ width: `${cardWidth}px` }}>
                  <div className="h-full rounded-2xl border border-gray-100 bg-white p-5 hover:border-[#970000]/30 transition-all duration-200">
                    <div className="w-9 h-9 rounded-xl bg-[#970000]/[0.08] flex items-center justify-center text-[#970000] mb-4">
                      <feature.icon className="h-4 w-4" />
                    </div>
                    <h3 className="text-[14px] font-semibold text-gray-900 mb-1.5">
                      {feature.title}
                    </h3>
                    <p className="text-[13px] text-gray-500 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4 mt-8">
            <button
              onClick={() => goTo(Math.max(currentIndex - 1, 0))}
              disabled={currentIndex === 0}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-[#970000] hover:text-[#970000] disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
              aria-label="Zurück"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>

            <div className="flex items-center gap-1.5">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    i === currentIndex ? 'w-6 bg-[#970000]' : 'w-3 bg-gray-200'
                  }`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={() => goTo(Math.min(currentIndex + 1, maxIndex))}
              disabled={currentIndex === maxIndex}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-[#970000] hover:text-[#970000] disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
              aria-label="Weiter"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </div>

      </div>
    </section>
  );
};

export default CourseFeatures;
